import { Button } from '@/components'
import Text from '@/components/Text'
import 'react-spring-bottom-sheet/dist/style.css'
import { ProgressBar } from '@/app/components/ProgressBar/ProgressBar'
import { CheckIcon, ChevronRightIcon, ChevronUpIcon } from 'lucide-react'
import {
  BottomSheetConfig,
  useBottomSheet,
} from '@/context/bottom-sheet-context'
import {
  ProgramRequirementSheetProps,
  viewModel as useViewModel,
} from './useViewModel'
import { useEffect, useMemo, useState } from 'react'
import { ProgramRequirementStep } from '@/types/utilityProgramProfileEligibility'
import { useLocation } from 'react-router-dom'
import {
  MultiProgramDeviceFlowProvider,
  MultiProgramOnboardingFlowProvider,
} from '@/app/context/MultiProgram/MultiProgramFlowContexts'
import { match } from 'ts-pattern'

export function programRequirementsSheetConfig(
  props: ProgramRequirementSheetProps,
  flow: 'onboarding' | 'devices',
) {
  const SheetFlowProvider = match(flow)
    .with('onboarding', () => MultiProgramOnboardingFlowProvider)
    .with('devices', () => MultiProgramDeviceFlowProvider)
    .exhaustive()

  return {
    children: (
      <SheetFlowProvider>
        <ProgramRequirementsSteps {...props} />
      </SheetFlowProvider>
    ),
    open: true,
    header: (
      <SheetFlowProvider>
        <ProgramRequirementsHeader {...props} />
      </SheetFlowProvider>
    ),
    footer: (
      <SheetFlowProvider>
        <ProgramRequirementsFooter {...props} />
      </SheetFlowProvider>
    ),
    blocking: false,
    snapPoints: ({ minHeight, headerHeight }) => [minHeight, headerHeight],
    preventDismiss: true,
  } satisfies BottomSheetConfig
}

export function ProgramRequirementsHeader({
  utilityProgram,
}: ProgramRequirementSheetProps) {
  const { programRequirements, routeToNextUncompletedStep } = useViewModel({
    utilityProgram,
  })
  const { sheetRef, sheetHeight, headerHeight } = useBottomSheet()
  // this needs to be tracked locally because the bottom sheet height updates
  // only after animations (too slow)
  const [isExpanded, setIsExpanded] = useState(false)
  const location = useLocation()
  const isDetailsPage = location.pathname.includes('/program-details')
  useEffect(() => {
    if (!sheetHeight || !headerHeight) {
      return
    }
    setIsExpanded(Math.floor(sheetHeight) > Math.ceil(headerHeight))
  }, [sheetHeight])
  if (!programRequirements) {
    return null
  }
  const seeMore = () => {
    sheetRef.current?.snapTo((snaps) => snaps.minHeight)
    setIsExpanded(true)
  }
  const seeLess = () => {
    sheetRef.current?.snapTo((snaps) => snaps.headerHeight)
    setIsExpanded(false)
  }

  return (
    <div className={'flex flex-col gap-2 items-start'}>
      <div className={'flex flex-row w-full justify-between items-center'}>
        <Text variant={'h3'}>Application Steps</Text>
        <div
          className={'flex flex-row items-center gap-1'}
          onClick={() => (!isExpanded ? seeMore() : seeLess())}
        >
          <Text variant={'link'} className={'w-fit'}>
            {!isExpanded ? 'See more' : 'See less'}{' '}
          </Text>
          <ChevronUpIcon
            size={20}
            className={`${
              isExpanded ? 'rotate-180' : ''
            } transition-all text-base-blue-500`}
          />
        </div>
      </div>
      <Text variant={'body2'}>
        To finish enrolling in {programRequirements.planName} you must complete
        the following steps.
      </Text>
      <div className={'w-full'}>
        <ProgressBar
          layoutId="program-requirements-progress-bar"
          step={programRequirements.step}
          numberOfSteps={programRequirements.numberOfSteps}
        />
      </div>
      {isDetailsPage && (
        <Button id={'program-apply-cta'} onClick={routeToNextUncompletedStep}>
          Apply Now
        </Button>
      )}
    </div>
  )
}

export function ProgramRequirementsFooter({
  utilityProgram,
}: ProgramRequirementSheetProps) {
  const { routeToNextUncompletedStep, saveForLater } = useViewModel({
    utilityProgram,
  })
  const location = useLocation()
  const isDetailsPage = location.pathname.includes('/program-details')
  return (
    <div className={'flex gap-4 sm:flex-row flex-col'}>
      {!isDetailsPage && (
        <Button id={'complete-all'} onClick={routeToNextUncompletedStep}>
          Start Applying
        </Button>
      )}
      <Button
        id={'save-for-later'}
        variant={'primary-subtle'}
        onClick={saveForLater}
      >
        Save for Later
      </Button>
    </div>
  )
}

export function ProgramRequirementsSteps({
  utilityProgram,
}: ProgramRequirementSheetProps) {
  const { programRequirements, routeToStep } = useViewModel({
    utilityProgram,
  })
  const nextUncompletedStep = useMemo(
    () => programRequirements?.steps.find((s) => !s.completed),
    [programRequirements?.steps],
  )
  const handleStepClick = (step: ProgramRequirementStep) => {
    if (!canStepRouteOnClick(step)) {
      return
    }
    routeToStep(step)
  }
  const canStepRouteOnClick = (step: ProgramRequirementStep) => {
    return nextUncompletedStep === step || step.completed
  }
  if (!programRequirements) {
    return null
  }
  return (
    <div className={'flex flex-col m-1'}>
      {programRequirements.steps.map((step, index) => (
        <div
          key={step.name}
          className={`flex flex-row items-center gap-4 p-4 rounded-lg ${
            canStepRouteOnClick(step)
              ? 'cursor-pointer hover:bg-themed-grey-100'
              : 'cursor-default'
          }`}
          onClick={() => handleStepClick(step)}
        >
          {step.completed ? (
            <div
              className={
                'bg-base-green-100 text-base-green-500 h-8 w-8 rounded-lg flex-none flex items-center justify-center'
              }
            >
              <CheckIcon size={18} />
            </div>
          ) : (
            <Text
              className={
                'bg-base-blue-100 text-base-blue-500 h-8 w-8 rounded-lg flex-none font-bold flex items-center justify-center'
              }
            >
              {index + 1}
            </Text>
          )}
          <Text>{step.name}</Text>
          {canStepRouteOnClick(step) && (
            <ChevronRightIcon
              className={'text-base-grey-400 ml-auto flex-none'}
              size={16}
            />
          )}
        </div>
      ))}
    </div>
  )
}
